import React, { useState } from "react";
import { Plus, Trash2, Edit2, Clock, X, Loader2, Car } from "lucide-react";
import {
  useGetDailySlotsQuery,
  useCreateDailySlotMutation,
  useUpdateDailySlotMutation,
  useDeleteDailySlotMutation,
} from "../store/api/adminApi";
import { useToast } from "../components/Toast";

function to12(value) {
  if (!value) return "";
  const [h, m] = value.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const hour = h % 12 || 12;
  return `${String(hour).padStart(2, "0")}:${String(m).padStart(2, "0")} ${ampm}`;
}

function to24(value) {
  if (!value) return "";
  const [time, ampm] = value.split(" ");
  let [h, m] = time.split(":").map(Number);
  if (ampm === "PM" && h !== 12) h += 12;
  if (ampm === "AM" && h === 12) h = 0;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function SlotModal({ mode = "add", slot = null, onClose }) {
  const isEdit = mode === "edit";
  const toast = useToast();

  const [form, setForm] = useState({
    startTime:    isEdit ? to24(slot?.startTime) : "",
    endTime:      isEdit ? to24(slot?.endTime)   : "",
    carsQuantity: isEdit ? String(slot?.carsQuantity || 1) : "1",
    isActive:     isEdit ? slot?.isActive !== false : true,
  });
  const [error, setError] = useState("");

  const [createSlot, { isLoading: isCreating }] = useCreateDailySlotMutation();
  const [updateSlot, { isLoading: isUpdating }] = useUpdateDailySlotMutation();
  const isSaving = isCreating || isUpdating;

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.startTime || !form.endTime) { setError("Start and end time are required."); return; }
    if (form.startTime >= form.endTime) { setError("End time must be after start time."); return; }

    const body = {
      startTime:    to12(form.startTime),
      endTime:      to12(form.endTime),
      carsQuantity: Number(form.carsQuantity) || 1,
      isActive:     form.isActive,
    };

    try {
      if (isEdit) {
        await updateSlot({ id: slot.id, ...body }).unwrap();
        toast.success("Time slot updated.");
      } else {
        await createSlot(body).unwrap();
        toast.success("Time slot created.");
      }
      onClose();
    } catch (err) {
      toast.error(err?.data?.error?.message || "Failed to save time slot.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-[400px] p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-[16px] font-manrope-bold text-gray-900">
            {isEdit ? "Edit time slot" : "Add time slot"}
          </h2>
          <button onClick={onClose} className="w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center transition-all">
            <X size={15} className="text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[12px] font-manrope-semibold text-gray-600 mb-1.5">Start time</label>
              <input
                type="time"
                value={form.startTime}
                onChange={set("startTime")}
                className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-[13px] font-manrope-regular text-gray-700 focus:outline-none focus:border-primary transition-all"
              />
            </div>
            <div>
              <label className="block text-[12px] font-manrope-semibold text-gray-600 mb-1.5">End time</label>
              <input
                type="time"
                value={form.endTime}
                onChange={set("endTime")}
                className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-[13px] font-manrope-regular text-gray-700 focus:outline-none focus:border-primary transition-all"
              />
            </div>
          </div>

          <div>
            <label className="block text-[12px] font-manrope-semibold text-gray-600 mb-1.5">Cars quantity (capacity per slot)</label>
            <div className="relative">
              <input
                type="number"
                min="1"
                value={form.carsQuantity}
                onChange={set("carsQuantity")}
                className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-[13px] font-manrope-regular text-gray-700 focus:outline-none focus:border-primary transition-all"
              />
              <Car size={13} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400" strokeWidth={1.6} />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" checked={form.isActive} onChange={set("isActive")} className="sr-only peer" />
              <div className="w-9 h-5 bg-gray-200 rounded-full peer peer-checked:bg-primary transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4" />
            </label>
            <span className="text-[13px] font-manrope-regular text-gray-600">Active — visible to customers</span>
          </div>

          {error && <p className="text-[12px] font-manrope-regular text-red-500 bg-red-50 px-4 py-2.5 rounded-xl">{error}</p>}

          <button
            type="submit"
            disabled={isSaving}
            className="w-full py-3 bg-primary rounded-xl text-white text-[14px] font-manrope-semibold hover:opacity-90 disabled:opacity-60 transition-all flex items-center justify-center gap-2"
          >
            {isSaving && <Loader2 size={15} className="animate-spin" />}
            {isSaving ? "Saving…" : isEdit ? "Save changes" : "Create slot"}
          </button>
        </form>
      </div>
    </div>
  );
}

function DeleteModal({ slot, onCancel, onConfirm, isDeleting }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl p-8 w-[340px] flex flex-col items-center text-center shadow-2xl">
        <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4">
          <Trash2 size={28} className="text-primary" strokeWidth={1.8} />
        </div>
        <h3 className="text-[16px] font-manrope-bold text-primary mb-2">Delete time slot?</h3>
        <p className="text-[12px] font-manrope-regular text-gray-400 mb-1">
          Remove <span className="font-manrope-semibold text-gray-700">{slot?.startTime} – {slot?.endTime}</span> from daily schedule.
        </p>
        <div className="flex gap-3 w-full mt-5">
          <button onClick={onCancel} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-[13px] font-manrope-semibold text-gray-600 hover:bg-gray-50 transition-all">Cancel</button>
          <button onClick={onConfirm} disabled={isDeleting} className="flex-1 py-2.5 rounded-xl bg-primary text-white text-[13px] font-manrope-semibold hover:opacity-90 disabled:opacity-60 transition-all flex items-center justify-center gap-2">
            {isDeleting && <Loader2 size={13} className="animate-spin" />}
            {isDeleting ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TimeSlots() {
  const [modal, setModal] = useState(null); // "add" | "edit" | null
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const toast = useToast();

  const { data, isLoading, isError } = useGetDailySlotsQuery();
  const [deleteSlot, { isLoading: isDeleting }] = useDeleteDailySlotMutation();

  const slots = data?.data || [];

  const handleDelete = async () => {
    try {
      await deleteSlot(deleteTarget.id).unwrap();
      setDeleteTarget(null);
      toast.success("Time slot deleted.");
    } catch {
      toast.error("Failed to delete time slot.");
    }
  };

  return (
    <>
      <div className="flex flex-col min-h-screen">
        <header className="flex items-center justify-between px-8 pt-7 pb-4">
          <div>
            <h1 className="text-[22px] font-manrope-bold text-gray-900">Time slots</h1>
            <p className="text-[12px] font-manrope-regular text-gray-400 mt-0.5">
              Manage daily recurring time slots available for custom bookings.
            </p>
          </div>
          <button
            onClick={() => setModal("add")}
            className="flex items-center gap-2 px-4 py-2.5 bg-primary rounded-xl text-white text-[13px] font-manrope-semibold hover:opacity-90 transition-all"
          >
            <Plus size={15} strokeWidth={2.2} />
            Add time slot
          </button>
        </header>

        <div className="flex-1 px-8 pb-8">
          {/* Info banner */}
          <div className="bg-primary/5 border border-primary/20 rounded-xl px-5 py-3.5 mb-5 flex items-start gap-3">
            <Clock size={16} className="text-primary mt-0.5 flex-shrink-0" strokeWidth={1.6} />
            <p className="text-[12px] font-manrope-regular text-gray-600 leading-relaxed">
              These time slots repeat <strong>every day</strong> and are shown to customers when they submit a custom booking request. Each slot's capacity is set by the <strong>cars quantity</strong> — if you have 10 cars, each slot can accept up to 10 bookings.
            </p>
          </div>

          <div className="bg-white rounded-2xl overflow-hidden">
            <div className="grid grid-cols-[2fr_1fr_1fr_1fr] px-5 py-3 border-b border-gray-50">
              {["Time slot", "Cars quantity", "Status", "Actions"].map((col) => (
                <span key={col} className="text-[12px] font-manrope-semibold text-gray-400">{col}</span>
              ))}
            </div>

            {isLoading && (
              <div className="flex items-center justify-center py-16 gap-2 text-gray-400">
                <Loader2 size={18} className="animate-spin" />
                <span className="text-[13px] font-manrope-regular">Loading…</span>
              </div>
            )}

            {isError && (
              <div className="flex items-center justify-center py-16 text-red-400 text-[13px] font-manrope-regular">
                Failed to load time slots. Please try again.
              </div>
            )}

            {!isLoading && !isError && slots.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <Clock size={36} className="text-gray-200" strokeWidth={1.2} />
                <p className="text-[13px] font-manrope-regular text-gray-400">No time slots yet. Add your first slot.</p>
              </div>
            )}

            {slots.map((slot) => (
              <div key={slot.id} className="grid grid-cols-[2fr_1fr_1fr_1fr] px-5 py-3.5 border-b border-gray-50 hover:bg-gray-50/50 transition-all items-center">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock size={15} className="text-primary" strokeWidth={1.6} />
                  </div>
                  <span className="text-[13px] font-manrope-semibold text-gray-800">
                    {slot.startTime} – {slot.endTime}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Car size={12} className="text-gray-400" strokeWidth={1.6} />
                  <span className="text-[13px] font-manrope-regular text-gray-600">{slot.carsQuantity}</span>
                </div>
                <span className={`text-[11px] font-manrope-semibold px-2.5 py-1 rounded-full w-fit ${slot.isActive ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-400"}`}>
                  {slot.isActive ? "Active" : "Inactive"}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => { setEditTarget(slot); setModal("edit"); }}
                    className="w-7 h-7 rounded-md border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-all"
                  >
                    <Edit2 size={13} className="text-gray-500" strokeWidth={1.6} />
                  </button>
                  <button
                    onClick={() => setDeleteTarget(slot)}
                    className="w-7 h-7 rounded-md bg-primary/10 flex items-center justify-center hover:bg-primary/20 transition-all"
                  >
                    <Trash2 size={14} className="text-primary" strokeWidth={1.6} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {modal && (
        <SlotModal
          mode={modal}
          slot={editTarget}
          onClose={() => { setModal(null); setEditTarget(null); }}
        />
      )}

      {deleteTarget && (
        <DeleteModal
          slot={deleteTarget}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
          isDeleting={isDeleting}
        />
      )}
    </>
  );
}
